/**
 * FAQSearchResultItem
 *
 * Renders a single FAQ result inside Backstage's global search page.
 * Matches CatalogSearchResultListItem structure.
 */

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { Link } from '@backstage/core-components';

const ANSWER_PREVIEW_CHARS = 180;

const useStyles = makeStyles()((_theme) => ({
  item: {
    display: 'flex',
  },
  flexContainer: {
    flexWrap: 'wrap' as const,
    flex: 1,
    minWidth: 0,
  },
  itemText: {
    width: '100%',
    wordBreak: 'break-word' as const,
    marginBottom: '0.75rem',
  },
  highlight: {
    '& mark': {
      backgroundColor: 'transparent',
      fontWeight: 700,
      color: 'inherit',
    },
  },
}));

export interface ResultHighlight {
  preTag: string;
  postTag: string;
  fields: {
    title?: string;
    text?: string;
  };
}

export interface FAQSearchResultItemProps {
  result: {
    title: string;
    text?: string;
    /** Answer-only excerpt supplied by the collator. Used as the display preview
     *  when no search highlight is available. Falls back to stripping `text`. */
    excerpt?: string;
    location: string;
    section?: string;
    category?: string;
  };
  highlight?: ResultHighlight;
  rank?: number;
}

function stripMarkdown(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`[^`]+`/g, m => m.slice(1, -1))
    .replace(/!\[.*?\]\(.*?\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/#{1,6}\s/g, '')
    .replace(/[*_~]+/g, '')
    .replace(/\n+/g, ' ')
    .trim();
}

/**
 * Converts a highlighted string (with preTag/postTag markers wrapping matched
 * terms) into a React element with <mark> elements so matched terms are visually
 * distinguished. The preTag/postTag come from Backstage's search engine and
 * wrap plain text content — they do not introduce user-controlled HTML.
 */
function renderHighlighted(text: string, preTag: string, postTag: string) {
  const parts = text.split(new RegExp(`(${escapeRegExp(preTag)}.*?${escapeRegExp(postTag)})`, 'g'));
  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith(preTag) && part.endsWith(postTag)) {
          const inner = part.slice(preTag.length, part.length - postTag.length);
          return <mark key={i}>{inner}</mark>;
        }
        return part;
      })}
    </>
  );
}

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export const FAQSearchResultItem = ({ result, highlight }: FAQSearchResultItemProps) => {
  const { classes } = useStyles();

  if (!result) return null;

  const { title, text, excerpt, location, section, category } = result;

  // Prefer highlight.fields.title for the link text so matched terms are bolded.
  const titleContent =
    highlight?.fields.title
      ? renderHighlighted(highlight.fields.title, highlight.preTag, highlight.postTag)
      : title;

  // Prefer highlight.fields.text for the excerpt (shows exact match context).
  // Fall back to the dedicated `excerpt` field (answer only), then to stripping `text`.
  let excerptContent: React.ReactNode;
  if (highlight?.fields.text) {
    excerptContent = renderHighlighted(highlight.fields.text, highlight.preTag, highlight.postTag);
  } else {
    const base = excerpt ?? (text ? stripMarkdown(text) : '');
    const plain =
      base.length > ANSWER_PREVIEW_CHARS
        ? `${base.slice(0, ANSWER_PREVIEW_CHARS).trimEnd()}…`
        : base;
    excerptContent = plain;
  }

  return (
    <div className={classes.item}>
      <ListItemIcon>
        <QuestionAnswerIcon />
      </ListItemIcon>
      <div className={classes.flexContainer}>
        <ListItemText
          className={`${classes.itemText} ${classes.highlight}`}
          primaryTypographyProps={{ variant: 'h6' }}
          primary={
            <Link noTrack to={location}>
              {titleContent}
            </Link>
          }
          secondary={
            <Typography component="span" color="textSecondary" variant="body2">
              {excerptContent}
            </Typography>
          }
        />
        <Box display="flex" gap={0.75} flexWrap="wrap">
          {section && (
            <Chip label={section} size="small" style={{ fontSize: 11, height: 20 }} />
          )}
          {category && (
            <Chip label={category} size="small" variant="outlined" style={{ fontSize: 11, height: 20 }} />
          )}
        </Box>
      </div>
    </div>
  );
};
